import { compileMolModels } from "@/FileSystem/LoadSaveMolModels/SaveMolModels/SaveMolModels";
import { TreeNodeList } from "@/TreeNodes/TreeNodeList/TreeNodeList";
import { getConvertedTxts } from "@/FileSystem/LoadSaveMolModels/SaveMolModels/SaveMolModelsUtils";
import { TreeNodeType } from "@/UI/Navigation/TreeView/TreeInterfaces";
import { getMoleculesFromStore } from "@/Store/StoreExternalAccess";
/**
 * Controls how selected molecules are processed by a plugin.
 *
 * - `separate`:  Each molecule is processed independently, producing one
 *                result per molecule. (e.g., protonating each compound)
 * - `pairwise`:  Proteins and compounds are cross-paired, producing one
 *                result per protein/compound combination. (e.g., docking)
 * - `together`:  All molecules are gathered into a single group and
 *                processed once collectively. (e.g., computing a bounding
 *                region around all molecules)
 */
export var ProcessingMode;
(function (ProcessingMode) {
    ProcessingMode["Separate"] = "separate";
    ProcessingMode["Pairwise"] = "pairwise";
    ProcessingMode["Together"] = "together";
})(ProcessingMode || (ProcessingMode = {}));
/**
 * MoleculeInput class.
 */
export class MoleculeInput {
    molsToConsider = {
        visible: true,
        selected: false,
        hiddenAndUnselected: false,
    };
    considerProteins = true;
    considerCompounds = true;
    /**
     * When true, all terminal molecule types (proteins, compounds, nucleic
     * acids, metals, solvent, ions, lipids, etc.) are gathered rather than
     * only proteins and compounds.
     */
    considerAllMoleculeTypes = false;
    /**
     * Controls how the gathered molecules are processed. When null, the
     * effective mode is inferred from considerProteins/considerCompounds
     * for backward compatibility.
     */
    processingMode = null;
    includeMetalsAsProtein = true;
    includeSolventAsProtein = true;
    includeNucleicAsProtein = true;
    allowUserToToggleIncludeMetalsAsProtein = true;
    allowUserToToggleIncludeSolventAsProtein = true;
    allowUserToToggleIncludeNucleicAsProtein = true;
    // When true, compounds belonging to a molecule that also has a protein are
    // merged into that molecule's receptor (e.g., to keep cofactors in the
    // binding site) rather than offered as separate dockable ligands.
    includeProteinAssociatedCompoundsAsProtein = false;
    allowUserToToggleIncludeProteinAssociatedCompoundsAsProtein = true;
    proteinFormat = "pdb";
    compoundFormat = "mol2";
    // No batching by default. null means batch according to nprocs.
    batchSize = undefined;
    // To identify this object as a molecule input, even when it's turned into a
    // JSON.
    isMoleculeInput = true;
    /**
     * The constructor for the MoleculeInput class.
     *
     * @param {IMoleculeInputParams} params  The parameters of the MoleculeInput
     *     object.
     */
    constructor(params) {
        if (params === undefined) {
            return;
        }
        if (params.molsToConsider !== undefined) {
            this.molsToConsider = params.molsToConsider;
        }
        if (params.considerProteins !== undefined) {
            this.considerProteins = params.considerProteins;
        }
        if (params.considerCompounds !== undefined) {
            this.considerCompounds = params.considerCompounds;
        }
        if (params.considerAllMoleculeTypes !== undefined) {
            this.considerAllMoleculeTypes = params.considerAllMoleculeTypes;
        }
        if (params.processingMode !== undefined) {
            this.processingMode = params.processingMode;
        }
        if (params.proteinFormat !== undefined) {
            this.proteinFormat = params.proteinFormat;
        }
        if (params.compoundFormat !== undefined) {
            this.compoundFormat = params.compoundFormat;
        }
        if (params.includeMetalsAsProtein !== undefined) {
            this.includeMetalsAsProtein = params.includeMetalsAsProtein;
        }
        if (params.includeSolventAsProtein !== undefined) {
            this.includeSolventAsProtein = params.includeSolventAsProtein;
        }
        if (params.includeNucleicAsProtein !== undefined) {
            this.includeNucleicAsProtein = params.includeNucleicAsProtein;
        }
        if (params.allowUserToToggleIncludeMetalsAsProtein !== undefined) {
            this.allowUserToToggleIncludeMetalsAsProtein =
                params.allowUserToToggleIncludeMetalsAsProtein;
        }
        if (params.allowUserToToggleIncludeSolventAsProtein !== undefined) {
            this.allowUserToToggleIncludeSolventAsProtein =
                params.allowUserToToggleIncludeSolventAsProtein;
        }
        if (params.allowUserToToggleIncludeNucleicAsProtein !== undefined) {
            this.allowUserToToggleIncludeNucleicAsProtein =
                params.allowUserToToggleIncludeNucleicAsProtein;
        }
        if (params.includeProteinAssociatedCompoundsAsProtein !== undefined) {
            this.includeProteinAssociatedCompoundsAsProtein =
                params.includeProteinAssociatedCompoundsAsProtein;
        }
        if (params.allowUserToToggleIncludeProteinAssociatedCompoundsAsProtein !==
            undefined) {
            this.allowUserToToggleIncludeProteinAssociatedCompoundsAsProtein =
                params.allowUserToToggleIncludeProteinAssociatedCompoundsAsProtein;
        }
        // If not specified, use the default batch size.
        if (params.batchSize !== undefined) {
            this.batchSize = params.batchSize;
        }
    }
    /**
     * The effective processing mode. When processingMode is not explicitly
     * set (null), infers the mode from the molecule type flags:
     *   - Both proteins and compounds considered => Pairwise
     *   - Otherwise => Separate
     * This preserves backward compatibility for existing plugins that never
     * set processingMode.
     *
     * @returns {ProcessingMode} The resolved processing mode.
     */
    get effectiveProcessingMode() {
        if (this.processingMode !== null) {
            return this.processingMode;
        }
        // Infer from flags for backward compatibility
        if (this.considerProteins && this.considerCompounds && !this.considerAllMoleculeTypes) {
            return ProcessingMode.Pairwise;
        }
        return ProcessingMode.Separate;
    }
    /**
     * Main entry point: gathers molecules according to the current
     * configuration and returns them structured per the processingMode.
     *
     * @returns {Promise<IProtCmpdTreeNodePair[] | FileInfo[]>} Pairwise
     *     pairs, or a flat list of FileInfo objects (for separate/together).
     */
    getProtAndCompoundPairs() {
        if (this.considerAllMoleculeTypes) {
            return this._gatherAllMoleculeTypes();
        }
        return this._gatherProteinAndCompoundTypes();
    }
    /**
     * Gathers only protein and compound types, then structures the result
     * according to the processing mode.
     *
     * @returns {Promise<IProtCmpdTreeNodePair[] | FileInfo[]>} The result.
     */
    async _gatherProteinAndCompoundTypes() {
        // Fold cofactors into the receptor only when explicitly enabled and both
        // proteins and compounds are in scope; otherwise compounds stay dockable.
        const mergeAssociated = this.includeProteinAssociatedCompoundsAsProtein &&
            this.considerProteins &&
            this.considerCompounds;
        const compiledMols = compileMolModels(this.molsToConsider, true, mergeAssociated);
        // Remove one or other, if required.
        if (!this.considerProteins) {
            compiledMols.nodeGroups = [];
        }
        if (!this.considerCompounds) {
            compiledMols.compoundsNodes = new TreeNodeList();
        }
        const protFileInfos = await this._convertProteinGroups(compiledMols.nodeGroups);
        const cmpdFileInfos = compiledMols.compoundsNodes
            ? await getConvertedTxts(compiledMols.compoundsNodes, this.compoundFormat, false)
            : [];
        const prots = protFileInfos.filter((p) => p !== undefined);
        const cmpds = cmpdFileInfos.filter((c) => c !== undefined);
        return this._structureResults(prots, cmpds);
    }
    /**
     * Gathers all molecule types (proteins, compounds, nucleic acids, metals,
     * solvent, ions, lipids, etc.) and structures the result according to the
     * processing mode.
     *
     * @returns {Promise<FileInfo[]>} A flat list of FileInfo objects.
     */
    async _gatherAllMoleculeTypes() {
        const compiledMols = compileMolModels(this.molsToConsider, true);
        if (!this.considerProteins) {
            compiledMols.nodeGroups = [];
        }
        if (!this.considerCompounds) {
            compiledMols.compoundsNodes = new TreeNodeList();
        }
        const allFileInfos = [];
        // Collect IDs of nodes already handled by compileMolModels so we
        // don't duplicate them when we sweep for "other" node types below.
        const handledIds = new Set();
        // Proteins (with filtering for metals/solvent/nucleic)
        for (const prots of compiledMols.nodeGroups) {
            const filteredProts = this._applyProteinFilters(prots);
            filteredProts.forEach((p) => {
                if (p.id)
                    handledIds.add(p.id);
            });
            const protFileInfos = await getConvertedTxts(filteredProts, this.proteinFormat, true);
            allFileInfos.push(...protFileInfos.filter((f) => f !== undefined));
        }
        // Compounds
        if (compiledMols.compoundsNodes &&
            compiledMols.compoundsNodes.length > 0) {
            compiledMols.compoundsNodes.forEach((c) => {
                if (c.id)
                    handledIds.add(c.id);
            });
            const cmpdFileInfos = await getConvertedTxts(compiledMols.compoundsNodes, this.compoundFormat, false);
            allFileInfos.push(...cmpdFileInfos.filter((f) => f !== undefined));
        }
        // Remaining terminal nodes of any type not already handled
        const remaining = this._gatherRemainingTerminals(handledIds);
        if (remaining.length > 0) {
            const otherFileInfos = await getConvertedTxts(remaining, "pdb", false);
            allFileInfos.push(...otherFileInfos.filter((f) => f !== undefined));
        }
        console.log("[MoleculeInput] Included terminal nodes:", allFileInfos.map((fi) => fi.treeNode?.title ?? fi.name));
        return allFileInfos;
    }
    /**
     * Converts protein node groups into FileInfo objects, applying the
     * metal/solvent/nucleic filters and merging each group into one file.
     *
     * @param {TreeNodeList[]} nodeGroups The protein node groups.
     * @returns {Promise<FileInfo[]>} One FileInfo per protein group.
     */
    async _convertProteinGroups(nodeGroups) {
        const promises = nodeGroups.map((prots) => {
            const filtered = this._applyProteinFilters(prots);
            return getConvertedTxts(filtered, this.proteinFormat, true).then((fileInfos) => fileInfos[0]);
        });
        return Promise.all(promises);
    }
    /**
     * Applies the includeMetals/Solvent/Nucleic filters to a protein
     * TreeNodeList.
     *
     * @param {TreeNodeList} prots The unfiltered protein node list.
     * @returns {TreeNodeList} The filtered list.
     */
    _applyProteinFilters(prots) {
        let filtered = prots;
        if (!this.includeMetalsAsProtein) {
            filtered = filtered.filter((p) => p.type !== TreeNodeType.Metal &&
                p.type !== TreeNodeType.Ions);
        }
        if (!this.includeSolventAsProtein) {
            filtered = filtered.filter((p) => p.type !== TreeNodeType.Solvent);
        }
        if (!this.includeNucleicAsProtein) {
            filtered = filtered.filter((p) => p.type !== TreeNodeType.Nucleic);
        }
        return filtered;
    }
    /**
     * Finds terminal nodes matching the visibility/selection criteria that
     * were not already handled by compileMolModels.
     *
     * @param {Set<string>} handledIds IDs of nodes already processed.
     * @returns {TreeNodeList} The remaining unhandled terminal nodes.
     */
    _gatherRemainingTerminals(handledIds) {
        const allMols = getMoleculesFromStore();
        let candidates = allMols.flattened;
        if (this.molsToConsider.visible &&
            !this.molsToConsider.hiddenAndUnselected) {
            candidates = candidates.filters.keepVisible(true);
        }
        else if (this.molsToConsider.selected &&
            !this.molsToConsider.hiddenAndUnselected) {
            candidates = candidates.filters.keepSelected(true);
        }
        return candidates.filters.onlyTerminal.filter((node) => {
            if (!node.id || handledIds.has(node.id))
                return false;
            if (!node.model)
                return false;
            return true;
        });
    }
    /**
     * Structures the gathered protein and compound FileInfos according to
     * the current processingMode.
     *
     * @param {FileInfo[]} prots  The protein FileInfos.
     * @param {FileInfo[]} cmpds  The compound FileInfos.
     * @returns {IProtCmpdTreeNodePair[] | FileInfo[]} Structured results.
     */
    _structureResults(prots, cmpds) {
        switch (this.effectiveProcessingMode) {
            case ProcessingMode.Pairwise:
                return this._buildPairwiseResults(prots, cmpds);
            case ProcessingMode.Together:
            case ProcessingMode.Separate:
            default:
                // Both together and separate return a flat list; the
                // distinction is in how the caller interprets the result
                // (one collective run vs. one run per item).
                if (prots.length > 0 && cmpds.length > 0) {
                    return [...prots, ...cmpds];
                }
                return cmpds.length > 0 ? cmpds : prots;
        }
    }
    /**
     * Builds pairwise protein/compound combinations.
     *
     * @param {FileInfo[]} prots  The protein FileInfos.
     * @param {FileInfo[]} cmpds  The compound FileInfos.
     * @returns {IProtCmpdTreeNodePair[] | FileInfo[]} Pairs if both exist,
     *     otherwise whichever list is non-empty.
     */
    _buildPairwiseResults(prots, cmpds) {
        if (prots.length > 0 && cmpds.length > 0) {
            const pairs = [];
            for (const prot of prots) {
                for (const cmpd of cmpds) {
                    pairs.push({ prot, cmpd });
                }
            }
            return pairs;
        }
        return cmpds.length > 0 ? cmpds : prots;
    }
}
